import { Typography } from "@/components/text";
import { MarkdownMessage } from "@/components/ui/message";
import { useBoolean } from "@/hooks/useBoolean";
import { cn } from "@/libraries/cn";
import { formatHumanReadableDate, fromYearToAge } from "@/libraries/date";
import type { AlertType } from "@/types";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import type React from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

interface AlertCard extends React.ComponentProps<"div"> {
	type: "my_outstanding" | "awaiting_response";
	message: AlertType;
	onAcceptInvitation: () => void;
	onDeclineInvitation: () => void;
}

const AlertCard = (props: AlertCard) => {
	const { type, message, onAcceptInvitation, onDeclineInvitation } = props;
	const { t } = useTranslation();
	const { value: isCanShowMore, setValue: onTextRender } = useBoolean();
	const {
		value: isShowMore,
		setTrue: onShowMore,
		setFalse: onShowLess,
	} = useBoolean();
	const isRequest = useMemo(() => message.tags === "request", [message]);
	const messageText = useMemo(() => {
		if (isRequest) {
			return [
				`@${message.user.fullname}`,
				t("systemMessages.hasRequestedAccess"),
				message.text
					? t("systemMessages.withMessage", { msg: message.text })
					: "",
			]
				.filter(Boolean)
				.join(" ");
		}
		return [message.text, message.invitation_message].join("\n\n");
	}, [
		isRequest,
		message.text,
		message.invitation_message,
		message.user.fullname,
		t,
	]);
	const title = useMemo(() => {
		if (message?.case?.title || message?.case?.patient) {
			if (message?.case?.patient?.name) {
				return [
					message?.case?.patient?.name,
					message?.case?.patient?.surname,
				].join(" ");
			}
			return message.case.title;
		}
		return message?.group?.name || message?.team?.name;
	}, [message]);
	const subTitle = useMemo(() => {
		if (message?.case?.year_of_birth || message?.case?.patient) {
			let dob = "";
			if (message.case?.year_of_birth) {
				dob = fromYearToAge(message.case.year_of_birth, " yo • ");
			}
			if (message.case.patient?.dob) {
				const age = fromYearToAge(message.case.patient.dob, "years");
				dob = `${age} yo • `;
			}
			const caseInfo = message.case.patient?.name
				? message.case.title
				: message.case.summary || "-";
			return dob + caseInfo;
		}
		return null;
	}, [message]);
	const description = useMemo(() => {
		if (message.tags === "request") {
			return `requests to join this case.\n${message?.text}`;
		}
		return message.text.replace(message?.user?.fullname || "", "");
	}, [message]);
	const members = useMemo(() => {
		const mentions = [{ id: message.user.id, name: message.user.fullname }];
		if (message.mentions?.length) {
			for (const mention of message.mentions) {
				mentions.push({
					id: mention.id,
					name: mention.fullname,
				});
			}
		}
		return mentions;
	}, [message.user, message.mentions]);
	return (
		<Card variant="outlined" className="w-full h-full !rounded-xl !shadow-sm">
			<div className="space-y-3 px-4 py-2">
				<div className="flex justify-between items-center">
					<div className="space-y-1">
						<Typography fontWeight={500} fontSize={14}>
							{title}
						</Typography>
						{subTitle ? (
							<Typography fontWeight={500} fontSize={12}>
								{subTitle}
							</Typography>
						) : null}
						<Typography fontWeight={400} fontSize={10}>
							{formatHumanReadableDate(message.updated_at)}
						</Typography>
					</div>
				</div>
				{description ? <Divider className="w-full" /> : null}
				<div className="flex flex-col gap-2">
					<MarkdownMessage
						className="max-w-[40rem]"
						text={messageText}
						members={members}
						lineClamp={isShowMore ? undefined : 3}
						onTextRender={onTextRender}
					/>
					{isCanShowMore && (
						<Button
							variant="text"
							color="primary"
							size="small"
							className={cn(
								"!w-fit [&_svg]:transition-transform [&_svg]:duration-300",
								{
									"[&_svg]:rotate-180": isShowMore,
									"[&_svg]:rotate-0": !isShowMore,
								},
							)}
							endIcon={<KeyboardArrowDownRoundedIcon />}
							onClick={isShowMore ? onShowLess : onShowMore}
						>
							{isShowMore ? t("showLess") : t("showMore")}
						</Button>
					)}
					{type === "my_outstanding" ? (
						<div className="flex justify-end items-center gap-3">
							<Button
								variant="contained"
								color="primary"
								size="small"
								className="!rounded-full"
								onClick={onAcceptInvitation}
							>
								{t("accept")}
							</Button>
							<Button
								variant="contained"
								color="error"
								size="small"
								className="!rounded-full"
								onClick={onDeclineInvitation}
							>
								{t("decline")}
							</Button>
						</div>
					) : null}
				</div>
			</div>
		</Card>
	);
};

export default AlertCard;
